import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { UserRole } from '../types';

/**
 * Custom hook to ensure member context is available for member-specific operations
 * Redirects to profile selection if no member profile is selected for member users
 */
export const useMemberContext = () => {
    const { user, selectedMemberProfile } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        // Only enforce member context for users with 'member' role
        // Don't redirect if we're already on the selection page
        if (user?.role === UserRole.MEMBER && !selectedMemberProfile && 
            window.location.pathname !== '/select-member-profile') {
            navigate('/select-member-profile');
        }
    }, [user, selectedMemberProfile, navigate]);

    return {
        user,
        memberProfile: selectedMemberProfile,
        isMemberUser: user?.role === UserRole.MEMBER,
        hasMemberContext: user?.role === UserRole.MEMBER ? !!selectedMemberProfile : true
    };
};

/**
 * Custom hook specifically for member-only pages
 * Ensures both authentication and member profile selection
 */
export const useRequiredMemberContext = () => {
    const { user, selectedMemberProfile } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }

        if (user.role === UserRole.MEMBER && !selectedMemberProfile && 
            window.location.pathname !== '/select-member-profile') {
            navigate('/select-member-profile');
            return;
        }
    }, [user, selectedMemberProfile, navigate]);

    return {
        user,
        memberProfile: selectedMemberProfile,
        isReady: user?.role === UserRole.MEMBER ? !!selectedMemberProfile : !!user
    };
};